import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../ui';

const STEPS = [
  {
    number: '01',
    title: 'Опишите идею словами',
    body: 'Одна фраза — «11 ночей по Ликийскому побережью на машине, море и античность, без спешки» — и AI собирает каркас: дни, базы, точки на карте. Или начните с чистого листа и добавляйте всё вручную — а если ещё не решили, куда, загляните в горячие туры и билеты.',
    accent: 'from-clay-400 to-clay-600',
    icon: '✨',
  },
  {
    number: '02',
    title: 'Доведите маршрут до ума',
    body: 'Перетаскивайте пункты между днями, ставьте время, добавляйте свои места на карту, прикрепляйте брони и билеты. Всё живёт в одном документе, а не в шести вкладках и переписке.',
    accent: 'from-lagoon-400 to-lagoon-600',
    icon: '🧩',
  },
  {
    number: '03',
    title: 'Съездите и загрузите фото',
    body: 'Забросьте всю папку с фотографиями разом — по геометкам и времени съёмки они сами встанут на нужные места и дни. Останется поправить пару штук и выбрать, что показывать другим.',
    accent: 'from-sun-400 to-clay-500',
    icon: '📸',
  },
  {
    number: '04',
    title: 'Поделитесь или спрячьте',
    body: 'Публичная версия показывает маршрут и места, но не даты, брони и номера билетов. Ссылку можно открыть даже без регистрации — а любой понравившийся маршрут взять за основу своего.',
    accent: 'from-ink-600 to-ink-800',
    icon: '🔗',
  },
];

/**
 * Sticky-scroll steps.
 *
 * The left column pins while the right column scrolls through the four steps —
 * the reader's position in the list is always visible, which is what makes a
 * long explanation feel short.
 */
export function HowItWorks() {
  const containerRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start center', 'end end'],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="how" className="relative py-24 md:py-36">
      <div className="container-page">
        <Reveal>
          <SectionHeading
            eyebrow="Как это работает"
            title={
              <>
                Четыре шага
                <br />
                от идеи до поездки
              </>
            }
            description="Никаких таблиц, заметок в мессенджере и папки «Турция_итог_финал_2». Один документ, который знает про ваш маршрут всё."
          />
        </Reveal>

        <div ref={containerRef} className="mt-16 grid gap-12 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,1fr)]">
          <div className="hidden lg:block">
            <div className="sticky top-32">
              <div className="relative h-64 w-full overflow-hidden rounded-2xl bg-gradient-to-br from-ink-900 to-ink-700 p-8 text-sand-100">
                <div className="aurora opacity-30" />
                <div className="relative">
                  <div className="display text-6xl">Travessa</div>
                  <p className="mt-4 max-w-xs text-sm leading-relaxed text-sand-300">
                    Маршрут, карта, документы, фотографии и приватность — собранные так, чтобы
                    ими было приятно пользоваться и в планировании, и в дороге.
                  </p>
                </div>
              </div>
              <div className="relative mt-8 h-1 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                <motion.div
                  style={reduced ? { scaleX: 1 } : { scaleX: lineScale }}
                  className="h-full origin-left rounded-full bg-gradient-to-r from-clay-500 to-lagoon-500"
                />
              </div>
            </div>
          </div>

          <ol className="space-y-5">
            {STEPS.map((step, index) => (
              <Reveal as="li" key={step.number} delay={index * 0.05}>
                <motion.div
                  whileHover={reduced ? undefined : { y: -3 }}
                  transition={{ duration: 0.25 }}
                  className="surface-card group flex gap-5 p-6 sm:p-8"
                >
                  <div
                    className={`grid size-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br ${step.accent} text-xl text-white shadow-lg`}
                  >
                    {step.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="eyebrow mb-1.5">Шаг {step.number}</div>
                    <h3 className="text-xl font-semibold tracking-tight sm:text-2xl">
                      {step.title}
                    </h3>
                    <p className="mt-2.5 leading-relaxed text-[var(--text-muted)]">{step.body}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
